import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Undo2, Redo2 } from "lucide-react";

function ToolbarButton({ onClick, active, children, title, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 rounded-md grid place-items-center transition-colors ${active ? "bg-[hsl(var(--brand-sage))] text-white" : "text-[hsl(var(--brand-muted))] hover:bg-[hsl(var(--brand-cream-deep))]"}`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose-sm max-w-none focus:outline-none min-h-[280px] px-4 py-3 text-[15px] leading-relaxed text-[hsl(var(--brand-ink))]",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync external value changes (e.g. when fetching guidelines on mount)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-md border border-[hsl(var(--brand-border))] bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-[hsl(var(--brand-cream-deep))] border-b border-[hsl(var(--brand-border))]">
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testid="rte-heading">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid="rte-bold">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid="rte-italic">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-[hsl(var(--brand-border))] mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid="rte-ul">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="rte-ol">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-[hsl(var(--brand-border))] mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo" testid="rte-undo">
          <Undo2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo" testid="rte-redo">
          <Redo2 className="w-4 h-4" />
        </ToolbarButton>
        {placeholder ? <div className="ml-auto text-xs text-[hsl(var(--brand-muted))] pr-2 hidden sm:block">{placeholder}</div> : null}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
